import { AuthService } from '@/services/abstract/AuthService';
import type {
  VerificationDTO,
  FinalizeVerifyDTO,
  FinalizeLoginDTO,
  InitLoginDTO,
  InitRegistrationDTO,
  LoginResult,
  LoginDTO,
  RecoveryVerificationDTO,
} from '@/types/dtos.types';
import type { FetcherService } from '@/services/abstract/FetcherService';
import { clearSessionToken } from '@/utils/sessionStorage';
import { authEndpoints } from '@/services/endpoints/auth';
import { createAuthInit, performOpaqueAuth } from '@/utils/opaque/opaque-login.utils';
import {
  createRegistrationRequest,
  performOpaqueRegistration,
} from '@/utils/opaque/opaque-registration.utils';

export class AuthServiceImpl extends AuthService {
  constructor(private readonly fetcher: FetcherService) {
    super();
  }

  // --- Authentication ---
  async login(data: LoginDTO): Promise<LoginResult> {
    const { username, password } = data;

    // Step 1: Create OPAQUE auth initialization (KE1) and get client instance
    const { ke1, client } = await createAuthInit(password);

    // Step 2: Send KE1 to server
    const serverResponse = await this.initLogin({ username, ke1 });

    // Step 3: Process server's KE2 and generate KE3 using same client instance
    const { sessionId, ke3 } = await performOpaqueAuth(client, username, serverResponse);

    // Step 4: Finalize login and verify session key
    const { twoFactorToken, maskedEmail, maskedPhone } = await this.finalizeLogin({
      sessionId,
      ke3,
    });

    // if (clientSessionKey !== sessionKey) {
    //   throw new Error('Session key mismatch - authentication failed');
    // }

    return { twoFactorToken, maskedEmail, maskedPhone };
  }

  async initLogin(data: { username: string; ke1: string }): Promise<InitLoginDTO> {
    return await this.fetcher.post(authEndpoints.initLogin, data);
  }

  async finalizeLogin(data: { sessionId: string; ke3: string }): Promise<FinalizeLoginDTO> {
    return await this.fetcher.post(authEndpoints.finalizeLogin, data);
  }

  async logout(): Promise<void> {
    await this.fetcher.post(authEndpoints.logoutUserAuth, {});
    clearSessionToken();
  }

  // --- Registration ---
  async beginRegistration(data: {
    username: string;
    otp: string;
  }): Promise<{ registrationToken: string }> {
    return await this.fetcher.post<{ registrationToken: string }>(
      authEndpoints.beginUserRegistrationAuth,
      data
    );
  }

  async setPassword(data: { username: string; password: string }): Promise<void> {
    const { username, password } = data;
    const email = username;
    try {
      // Step 1: Create OPAQUE registration request and get client instance
      const { registrationRequest, client } = await createRegistrationRequest(password);

      // Step 2: Send to server for OPRF evaluation
      const serverResponse = await this.initRegistration({
        username,
        registrationRequest,
      });

      // Step 3: Finalize registration using same client instance and send record to server
      const { sessionId, registrationRecord } = await performOpaqueRegistration(
        client,
        serverResponse,
        email
      );

      await this.finalizeRegistration({ sessionId, registrationRecord });
    } finally {
      // SECURITY: Clear password from memory
      data.password = '';
    }
  }

  async initRegistration(data: {
    username: string;
    registrationRequest: string;
  }): Promise<InitRegistrationDTO> {
    return await this.fetcher.post(authEndpoints.initRegistration, data);
  }

  async finalizeRegistration(data: {
    sessionId: string;
    registrationRecord: string;
  }): Promise<void> {
    await this.fetcher.post(authEndpoints.finalizeRegistration, data);
  }

  async confirmEmail(data: { code: string }): Promise<void> {
    await this.fetcher.post(authEndpoints.verifyUserEmailAuth, data);
  }

  async resendEmailCode(): Promise<VerificationDTO> {
    return await this.fetcher.post<VerificationDTO>(authEndpoints.resendUserEmailCodeAuth);
  }

  async sendEmailCode(): Promise<VerificationDTO> {
    return await this.fetcher.post(authEndpoints.sendEmailCode);
  }

  async confirmPhone(data: { code: string }): Promise<void> {
    await this.fetcher.post(authEndpoints.confirmUserPhoneAuth, data);
  }

  async resendPhoneCode(): Promise<VerificationDTO> {
    return await this.fetcher.post(authEndpoints.resendUserPhoneCodeAuth);
  }

  async sendPhoneCode(): Promise<VerificationDTO> {
    return await this.fetcher.post(authEndpoints.sendUserPhoneCodeAuth);
  }

  // --- Two-Factor Authentication (2FA) ---
  async selectTwoFactorMethod(data: {
    twoFactorToken: string;
    method: 'sms' | 'email';
  }): Promise<VerificationDTO> {
    return await this.fetcher.post(authEndpoints.selectUserTwoFactorMethodAuth, data);
  }

  async verifyTwoFactorCode(data: {
    code: string;
    twoFactorToken: string;
    method: 'sms' | 'email';
  }): Promise<FinalizeVerifyDTO> {
    return await this.fetcher.post(authEndpoints.verifyUserTwoFactorCodeAuth, data);
  }

  async resendTwoFactorCode(data: {
    method: 'sms' | 'email';
    twoFactorToken: string;
  }): Promise<VerificationDTO> {
    return await this.fetcher.post(authEndpoints.resendUserTwoFactorCodeAuth, data);
  }

  // --- Password Recovery ---

  async forgotPassword(data: { emailAddress: string }): Promise<void> {
    await this.fetcher.post(authEndpoints.forgotPassword, data);
  }

  async verifyRecoveryOtp(data: { emailAddress: string; code: string }): Promise<RecoveryVerificationDTO> {
    return await this.fetcher.post<RecoveryVerificationDTO>(authEndpoints.verifyRecoveryCode, data);
  }

  async sendRecoveryTwoFactor(data: { recoveryToken: string }): Promise<VerificationDTO> {
    return await this.fetcher.post<VerificationDTO>(authEndpoints.recoverySendTwoFactor, {
      ...data,
      method: 'sms',
    });
  }

  async verifyRecoveryTwoFactor(data: { recoveryToken: string; code: string }): Promise<void> {
    await this.fetcher.post(authEndpoints.recoveryVerifyTwoFactor, {
      ...data,
      method: 'sms',
    });
  }
}
